import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import HeaderBar from "../components/HeaderBar";
import MenuBar from "../components/MenuBar";
import { hasPermission } from "../config/permissions";
import { getVisibleAppBranches, normalizeBranchValue } from "../config/appBranches";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  WORKFLOW,
  getWorkflowStatus,
  allowedNextStatuses,
  slaBand,
  slaBandClass,
} from "../utils/pendingOrderWorkflow";
import { sendPendingOrderPartyAttachmentEmail } from "../services/pendingOrdersEmailApi";
import { partyEmailStatusBadge, toastPartyEmailResult } from "../utils/pendingOrderEmailUi";

const KEY_DRAFT = "buyer_order_draft";
const KEY_LAST_ORDER = "buyer_last_order";

function toBool(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(s)) return true;
    if (["false", "0", "no", "n"].includes(s)) return false;
  }
  return null;
}

function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === "function") {
    const d = v.toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (v instanceof Date) return !isNaN(v.getTime()) ? v : null;
  if (typeof v === "number") return !isNaN(new Date(v).getTime()) ? new Date(v) : null;
  if (typeof v === "string") {
    const d = new Date(v);
    return !isNaN(d.getTime()) ? d : null;
  }
  return null;
}

function getFieldString(obj, keys, fallback = "") {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim()) return String(v).trim();
  }
  return fallback;
}

function computePendingFlag(orderDoc) {
  if (getWorkflowStatus(orderDoc) === WORKFLOW.RECEIVED) return false;

  const status = String(orderDoc?.status || "").toLowerCase();
  const cancelled = status.includes("cancel");
  if (cancelled) return false;

  const received = toBool(orderDoc?.orderReceived ?? orderDoc?.received);
  if (received === true) return false;
  if (received === false) return true;

  // If received flag is missing/unknown, infer from status.
  if (status.includes("received")) return false;
  if (status.includes("approved")) return false;
  if (status.includes("rejected")) return false;
  if (status.includes("pending")) return true;

  // Unknown status: treat as pending so the list doesn't look empty.
  return true;
}

function fmtDateForInput(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtDateTime(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function ymd(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PendingOrdersPage() {
  const navigate = useNavigate();
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const visibleBranches = useMemo(() => getVisibleAppBranches(storedUser), [storedUser]);

  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editingAttachments, setEditingAttachments] = useState([]);
  /** Files chosen in the modal; uploaded to Storage + `attachments` when the order is saved. */
  const [stagedFiles, setStagedFiles] = useState([]);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const storage = useMemo(() => getStorage(), []);

  const role = useMemo(() => String(storedUser?.role || "").toLowerCase(), [storedUser]);

  /** Edit: anyone with pending_orders permission, or these roles (incl. buyer / storekeeper from mobile app). */
  const canEdit = useMemo(() => {
    if (hasPermission(storedUser, "pending_orders")) return true;
    return ["admin", "accounts", "staff", "buyer", "storekeeper", "boss"].includes(role);
  }, [storedUser, role]);

  const canCancel = role === "admin" || role === "accounts";
  const canAttach = useMemo(() => {
    if (hasPermission(storedUser, "pending_orders")) return true;
    return ["admin", "accounts", "staff", "buyer", "storekeeper", "boss"].includes(role);
  }, [storedUser, role]);

  const audit = async (action, entityId, extra = {}) => {
    try {
      const u = auth.currentUser;
      await addDoc(collection(db, "auditLogs"), {
        entity: "order",
        entityId,
        action,
        by: u?.uid || null,
        byEmail: u?.email || null,
        byName: u?.displayName || null,
        role: role || "unknown",
        extra,
        timestamp: serverTimestamp(),
      });
    } catch (e) {
      console.warn("auditLogs write failed", e);
    }
  };

  // ---- Keyboard navigation refs (Enter moves to next field) ----
  const branchRef = useRef(null);
  const dateRef = useRef(null);
  const supplierRef = useRef(null);
  const brandRef = useRef(null);
  const qtyRef = useRef(null);
  const amountRef = useRef(null);
  const remarksRef = useRef(null);
  const sendTypeRef = useRef(null);
  const preparedByRef = useRef(null);
  const submitRef = useRef(null);

  // Pending list state
  const [selectedBranch, setSelectedBranch] = useState("Main");

  useEffect(() => {
    if (!visibleBranches.length) return;
    const allowed = new Set(visibleBranches.map((b) => b.value));
    if (!allowed.has(selectedBranch)) {
      setSelectedBranch(visibleBranches[0].value);
    }
  }, [visibleBranches, selectedBranch]);
  const [orders, setOrders] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  // ---- Advanced filters (pending list) ----
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterPreparedBy, setFilterPreparedBy] = useState("");
  const [filterOrderType, setFilterOrderType] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [filterAmountMin, setFilterAmountMin] = useState("");
  const [filterAmountMax, setFilterAmountMax] = useState("");
  const [agingAlertEnabled, setAgingAlertEnabled] = useState(false);
  const [agingThresholdDays, setAgingThresholdDays] = useState(14);

  /** 'pending' = all open orders for branch; 'approval' = submitted & needs approver match */
  const [listTab, setListTab] = useState("pending");
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortBy, setSortBy] = useState("date");
  const [timelineFor, setTimelineFor] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineRows, setTimelineRows] = useState([]);
  const [approverEmail, setApproverEmail] = useState("");
  const [linkedPoRef, setLinkedPoRef] = useState("");
  const [linkedGrnRef, setLinkedGrnRef] = useState("");
  const [transitionOpen, setTransitionOpen] = useState(null);
  const [transitionComment, setTransitionComment] = useState("");

  const currentUserEmail = String(auth.currentUser?.email || "").toLowerCase();

  useEffect(() => {
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(500));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOrders(list);
      setLoadingPending(false);
    });
    return () => unsub();
  }, []);

  const pendingList = useMemo(() => {
    const filtered = [];
    for (const po of orders) {
      const branch = normalizeBranchValue(po?.branch);
      if (branch !== selectedBranch) continue;
      if (!computePendingFlag(po)) continue;

      const orderDate =
        toDate(po?.date ?? po?.orderDate ?? po?.createdAt) ??
        // fallback: keep stable even if missing date
        new Date(po?.createdAt?.seconds ? po.createdAt.seconds * 1000 : Date.now());

      const brand = getFieldString(po, ["brand"], "N/A");
      const preparedBy = getFieldString(po, ["orderPreparedBy", "preparedBy", "prepared_by"], "N/A");
      const sendType = getFieldString(po, ["sendType", "orderType", "type"], "");
      const rawAmount = po?.orderAmount ?? po?.amount;
      const amount = typeof rawAmount === "number" ? rawAmount : parseFloat(String(rawAmount ?? "")) || 0;

      // ---- apply filters ----
      const orderYmd = ymd(orderDate);
      if (filterFrom && orderYmd && orderYmd < filterFrom) continue;
      if (filterTo && orderYmd && orderYmd > filterTo) continue;
      if (filterPreparedBy && preparedBy !== filterPreparedBy) continue;
      if (filterOrderType && sendType !== filterOrderType) continue;
      if (filterBrand && !brand.toLowerCase().includes(filterBrand.trim().toLowerCase())) continue;
      const minA = filterAmountMin.trim() ? parseFloat(filterAmountMin) : null;
      const maxA = filterAmountMax.trim() ? parseFloat(filterAmountMax) : null;
      if (minA != null && Number.isFinite(minA) && amount < minA) continue;
      if (maxA != null && Number.isFinite(maxA) && amount > maxA) continue;

      const wf = getWorkflowStatus(po);
      const pendingDays = Math.floor((Date.now() - orderDate.getTime()) / (1000 * 60 * 60 * 24));

      filtered.push({
        id: po.id,
        raw: po,
        workflow: wf,
        pendingDays,
        sla: slaBand(pendingDays),
        invoiceNo: getFieldString(po, ["invoiceNo", "invoiceNoStr", "invoiceNumber", "invoiceNumberStr", "invoice_no"], "N/A"),
        supplier: getFieldString(po, ["supplier", "party", "vendor"], "N/A"),
        brand,
        date: orderDate,
        preparedBy,
        sendType,
        amount,
        attachments: Array.isArray(po?.attachments) ? po.attachments : [],
        linkedPo: getFieldString(po, ["linkedPoRef", "linkedPo", "purchaseOrderRef"], ""),
        linkedGrn: getFieldString(po, ["linkedGrnRef", "linkedGrn", "grnRef"], ""),
        approverEmail: getFieldString(po, ["approverEmail"], ""),
        slNo: po.slNo ?? po.sl ?? null,
        partyEmailStatus: po.partyEmailStatus || null,
        partyEmailLastTo: po.partyEmailLastTo || null,
        partyEmailLastError: po.partyEmailLastError || null,
        partyEmailSkipReason: po.partyEmailSkipReason || null,
      });
    }

    let out = filtered;
    if (listTab === "approval") {
      out = filtered.filter((row) => {
        if (row.workflow !== WORKFLOW.SUBMITTED) return false;
        const ap = String(row.approverEmail || "").trim().toLowerCase();
        if (!ap) return true;
        return ap === currentUserEmail;
      });
    }

    out.sort((a, b) => {
      if (sortBy === "aging") return (b.pendingDays ?? 0) - (a.pendingDays ?? 0);
      return b.date.getTime() - a.date.getTime();
    });

    return out;
  }, [
    orders,
    selectedBranch,
    filterFrom,
    filterTo,
    filterPreparedBy,
    filterOrderType,
    filterBrand,
    filterAmountMin,
    filterAmountMax,
    listTab,
    sortBy,
    currentUserEmail,
  ]);

  const pendingCount = useMemo(() => pendingList.length, [pendingList]);

  // Branch-wise summary cards (pending/received today/cancelled today + pending amount)
  const branchSummary = useMemo(() => {
    const today = new Date();
    const todayKey = ymd(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
    const base = {};
    for (const b of visibleBranches) {
      base[b.value] = { branch: b.value, pending: 0, receivedToday: 0, cancelledToday: 0, pendingAmount: 0 };
    }

    for (const po of orders) {
      const branch = normalizeBranchValue(po?.branch);
      if (!base[branch]) continue;
      const status = String(po?.status ?? "").trim().toLowerCase();
      const isCancelled = status.includes("cancel");
      const isPending = computePendingFlag(po);
      const received = toBool(po?.orderReceived ?? po?.received);

      const dt = toDate(po?.date ?? po?.orderDate ?? po?.createdAt);
      const key = dt ? ymd(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())) : "";

      const rawAmount = po?.orderAmount ?? po?.amount;
      const amount = typeof rawAmount === "number" ? rawAmount : parseFloat(String(rawAmount ?? "")) || 0;

      if (isPending) {
        base[branch].pending += 1;
        base[branch].pendingAmount += amount || 0;
      }
      if (received === true || status.includes("received")) {
        if (key === todayKey) base[branch].receivedToday += 1;
      }
      if (isCancelled) {
        if (key === todayKey) base[branch].cancelledToday += 1;
      }
    }
    return visibleBranches.map((b) => base[b.value]);
  }, [orders, visibleBranches]);

  // Optional aging alert for current filtered pending list
  useEffect(() => {
    if (!agingAlertEnabled) return;
    if (!pendingList.length) return;
    const threshold = Math.max(1, Number(agingThresholdDays) || 14);
    const maxDays = pendingList.reduce((m, o) => Math.max(m, Math.floor((Date.now() - o.date.getTime()) / (1000 * 60 * 60 * 24))), 0);
    if (maxDays >= threshold) {
      toast(`Warning: ${maxDays} day(s) pending (threshold ${threshold})`, { icon: "⚠️" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agingAlertEnabled, agingThresholdDays, selectedBranch, pendingCount]);

  // Order creation form state (port of buyer_screen.dart essentials)
  const _maxQty = 999999;
  const _maxAmount = 999999.99;

  const sanitizeQty = (v) => {
    const digits = String(v ?? "").replace(/\D/g, "");
    return digits.slice(0, 6);
  };

  const [formBranch, setFormBranch] = useState("Main");
  const [slNo, setSlNo] = useState(1);

  const [supplierOptions, setSupplierOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [orderTypeOptions, setOrderTypeOptions] = useState([]);
  const [preparedByOptions, setPreparedByOptions] = useState([]);
  const [supplierCounts, setSupplierCounts] = useState({});
  const [brandCounts, setBrandCounts] = useState({});

  const [supplier, setSupplier] = useState("");
  const [supplierMenuOpen, setSupplierMenuOpen] = useState(false);
  /** Keyboard highlight in supplier dropdown (-1 = none). */
  const [supplierHighlightIdx, setSupplierHighlightIdx] = useState(-1);
  const supplierWrapRef = useRef(null);
  const supplierListboxRef = useRef(null);
  const [brand, setBrand] = useState("");
  const [qty, setQty] = useState("");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [sendType, setSendType] = useState("");
  const [preparedBy, setPreparedBy] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [submitting, setSubmitting] = useState(false);

  const draftSaveTimer = useRef(null);

  const loadSlNo = async () => {
    try {
      const snap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(1)));
      if (snap.empty) {
        setSlNo(1);
        return;
      }
      const data = snap.docs[0].data() || {};
      const prev = data.slNo;
      const next = typeof prev === "number" ? prev + 1 : (parseInt(String(prev ?? "0"), 10) || 0) + 1;
      setSlNo(next);
    } catch {
      setSlNo(1);
    }
  };

  const loadMasters = async () => {
    // Supplier choices = party master (`parties` collection), same names used for email + ledger.
    try {
      const snap = await getDocs(collection(db, "parties"));
      const list = snap.docs
        .map((d) => {
          const x = d.data() || {};
          return String(x.partyName ?? x.partyname ?? x.name ?? x.supplierName ?? x.vendorName ?? "").trim();
        })
        .filter(Boolean);
      const uniq = [...new Set(list)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      setSupplierOptions(uniq);
    } catch {
      setSupplierOptions([]);
    }

    // Brands from recent orders
    try {
      const snap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(300)));
      const sCounts = {};
      const set = new Set();
      const bCounts = {};
      for (const d of snap.docs) {
        const data = d.data() || {};
        const s = String(data.supplier ?? "").trim();
        if (s) sCounts[s] = (sCounts[s] || 0) + 1;
        const b = String(d.data()?.brand ?? "").trim();
        if (b) {
          set.add(b);
          bCounts[b] = (bCounts[b] || 0) + 1;
        }
      }
      setSupplierCounts(sCounts);
      setBrandCounts(bCounts);
      setBrandOptions([...set]);
    } catch {
      setBrandOptions([]);
    }

    // Order types
    try {
      const snap = await getDocs(collection(db, "orderTypes"));
      const list = snap.docs
        .map((d) => {
          const x = d.data() || {};
          return String(x.name ?? x.type ?? "").trim();
        })
        .filter(Boolean)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setOrderTypeOptions(list);
    } catch {
      setOrderTypeOptions([]);
    }

    // Prepared by (staff preferred; fallback to users)
    try {
      const snap = await getDocs(collection(db, "staff"));
      const list = snap.docs
        .map((d) => {
          const x = d.data() || {};
          return String(x.name ?? x.username ?? x.fullName ?? "").trim();
        })
        .filter(Boolean)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      if (list.length) {
        setPreparedByOptions(list);
        return;
      }
    } catch {
      // ignore
    }

    try {
      const snap = await getDocs(query(collection(db, "users"), limit(200)));
      const list = snap.docs
        .map((d) => {
          const x = d.data() || {};
          return String(x.username ?? x.name ?? "").trim() || String(x.email ?? "").trim();
        })
        .filter(Boolean)
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      setPreparedByOptions(list);
    } catch {
      setPreparedByOptions([]);
    }
  };

  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(KEY_DRAFT);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (!d || typeof d !== "object") return;
      if (d.branch) setFormBranch(d.branch);
      if (d.supplier) setSupplier(d.supplier);
      if (d.brand) setBrand(d.brand);
      if (d.qty != null) setQty(String(d.qty));
      if (d.amount != null) setAmount(String(d.amount));
      if (d.remarks != null) setRemarks(String(d.remarks));
      if (d.sendType) setSendType(d.sendType);
      if (d.preparedBy) setPreparedBy(d.preparedBy);
      if (d.date) {
        const dt = toDate(d.date);
        if (dt) setSelectedDate(dt);
      }
    } catch {
      // ignore
    }
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(KEY_DRAFT);
    } catch {
      // ignore
    }
  };

  const saveDraft = () => {
    try {
      const payload = {
        branch: formBranch,
        supplier,
        brand,
        qty,
        amount,
        remarks,
        sendType,
        preparedBy,
        date: selectedDate?.toISOString?.() ?? null,
      };
      localStorage.setItem(KEY_DRAFT, JSON.stringify(payload));
    } catch {
      // ignore
    }
  };

  const saveLastOrder = () => {
    try {
      const payload = {
        branch: formBranch,
        supplier: supplier || null,
        brand: brand || null,
        qty: qty || null,
        amount: amount || null,
        remarks: remarks || null,
        sendType: sendType || null,
        preparedBy: preparedBy || null,
      };
      localStorage.setItem(KEY_LAST_ORDER, JSON.stringify(payload));
    } catch {
      // ignore
    }
  };

  const loadLastOrder = () => {
    try {
      const raw = localStorage.getItem(KEY_LAST_ORDER);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (!d || typeof d !== "object") return;
      if (d.branch) setFormBranch(d.branch);
      if (d.supplier) setSupplier(d.supplier);
      if (d.brand) setBrand(d.brand);
      if (d.qty != null) setQty(String(d.qty));
      if (d.amount != null) setAmount(String(d.amount));
      if (d.remarks != null) setRemarks(String(d.remarks));
      if (d.sendType) setSendType(d.sendType);
      if (d.preparedBy) setPreparedBy(d.preparedBy);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    // Keep form branch aligned initially with pending branch.
    setFormBranch(selectedBranch);
  }, [selectedBranch]);

  useEffect(() => {
    loadMasters();
    loadSlNo();
    loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const supplierOptionsRanked = useMemo(() => {
    const base = Array.isArray(supplierOptions) ? [...supplierOptions] : [];
    // De-dupe
    const uniq = [...new Set(base.map((s) => String(s).trim()).filter(Boolean))];
    uniq.sort((a, b) => {
      const ca = Number(supplierCounts?.[a] || 0);
      const cb = Number(supplierCounts?.[b] || 0);
      if (cb !== ca) return cb - ca; // frequent first
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
    return uniq;
  }, [supplierOptions, supplierCounts]);

  /** Datalist does not show large party lists in browsers — use this for the picker panel. */
  const supplierPickList = useMemo(() => {
    const q = supplier.trim().toLowerCase();
    if (!q) return supplierOptionsRanked;
    return supplierOptionsRanked.filter((s) => s.toLowerCase().includes(q));
  }, [supplier, supplierOptionsRanked]);

  const brandOptionsRanked = useMemo(() => {
    const base = Array.isArray(brandOptions) ? [...brandOptions] : [];
    const uniq = [...new Set(base.map((s) => String(s).trim()).filter(Boolean))];
    uniq.sort((a, b) => {
      const ca = Number(brandCounts?.[a] || 0);
      const cb = Number(brandCounts?.[b] || 0);
      if (cb !== ca) return cb - ca;
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
    return uniq;
  }, [brandOptions, brandCounts]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (supplierMenuOpen) {
        e.preventDefault();
        setSupplierMenuOpen(false);
        return;
      }
      setOrderModalOpen(false);
    };
    if (orderModalOpen) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [orderModalOpen, supplierMenuOpen]);

  useEffect(() => {
    if (!orderModalOpen) setStagedFiles([]);
  }, [orderModalOpen]);

  useEffect(() => {
    if (!orderModalOpen) setSupplierMenuOpen(false);
  }, [orderModalOpen]);

  useEffect(() => {
    if (!supplierMenuOpen) return;
    const fn = (e) => {
      if (supplierWrapRef.current && !supplierWrapRef.current.contains(e.target)) {
        setSupplierMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [supplierMenuOpen]);

  useEffect(() => {
    if (!supplierMenuOpen) {
      setSupplierHighlightIdx(-1);
      return;
    }
    setSupplierHighlightIdx((i) => {
      if (supplierPickList.length === 0) return -1;
      if (i < 0) return -1;
      return Math.min(i, supplierPickList.length - 1);
    });
  }, [supplierMenuOpen, supplierPickList]);

  useEffect(() => {
    if (!supplierMenuOpen || supplierHighlightIdx < 0) return;
    const root = supplierListboxRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-supplier-option-index="${supplierHighlightIdx}"]`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [supplierHighlightIdx, supplierMenuOpen, supplierPickList]);

  useEffect(() => {
    if (!orderModalOpen) return;
    // Focus first field after modal opens.
    const t = requestAnimationFrame(() => {
      try {
        branchRef.current?.focus?.();
      } catch {
        // ignore
      }
    });
    return () => cancelAnimationFrame(t);
  }, [orderModalOpen]);

  const focusNext = (refObj) => {
    const next = refObj?.current;
    if (!next) return;
    setTimeout(() => {
      try {
        next.focus?.();
      } catch {
        // ignore
      }
    }, 0);
  };

  // Debounced draft save (only while modal open)
  useEffect(() => {
    if (!orderModalOpen) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(() => saveDraft(), 350);
    return () => {
      if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderModalOpen, formBranch, supplier, brand, qty, amount, remarks, sendType, preparedBy, selectedDate]);

  const openNewOrder = () => {
    setEditingOrderId(null);
    setEditingAttachments([]);
    setStagedFiles([]);
    setSupplierMenuOpen(false);
    setApproverEmail("");
    setLinkedPoRef("");
    setLinkedGrnRef("");
    setOrderModalOpen(true);
    loadDraft();
    loadSlNo();
    setTimeout(() => branchRef.current?.focus?.(), 0);
  };

  const openEditOrder = (row) => {
    const po = row?.raw || {};
    setEditingOrderId(row.id);
    setEditingAttachments(Array.isArray(po.attachments) ? po.attachments : []);
    setStagedFiles([]);
    setSupplierMenuOpen(false);
    setOrderModalOpen(true);

    setFormBranch(normalizeBranchValue(po.branch));
    const dt = toDate(po.date ?? po.orderDate ?? po.createdAt) ?? new Date();
    setSelectedDate(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    setSupplier(getFieldString(po, ["supplier", "party", "vendor"], ""));
    setBrand(String(po.brand ?? "").trim());
    setQty(String(po.quantity ?? "").trim());
    {
      const a = po.orderAmount ?? po.amount;
      setAmount(a != null && a !== "" ? String(a) : "");
    }
    setRemarks(String(po.remarks ?? "").trim());
    setSendType(getFieldString(po, ["sendType", "orderType", "type"], ""));
    setPreparedBy(getFieldString(po, ["orderPreparedBy", "preparedBy", "prepared_by"], ""));
    setSlNo(typeof po.slNo === "number" ? po.slNo : parseInt(String(po.slNo ?? "0"), 10) || 0);
    setApproverEmail(getFieldString(po, ["approverEmail"], ""));
    setLinkedPoRef(getFieldString(po, ["linkedPoRef", "linkedPo", "purchaseOrderRef"], ""));
    setLinkedGrnRef(getFieldString(po, ["linkedGrnRef", "linkedGrn", "grnRef"], ""));

    setTimeout(() => branchRef.current?.focus?.(), 0);
  };

  const duplicateOrder = (row) => {
    const po = row?.raw || {};
    setEditingOrderId(null);
    setEditingAttachments([]);
    setStagedFiles([]);
    setSupplierMenuOpen(false);
    setOrderModalOpen(true);
    setFormBranch(normalizeBranchValue(po.branch));
    const dt = toDate(po.date ?? po.orderDate ?? po.createdAt) ?? new Date();
    setSelectedDate(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    setSupplier(getFieldString(po, ["supplier", "party", "vendor"], ""));
    setBrand(String(po.brand ?? "").trim());
    setQty(String(po.quantity ?? "").trim());
    {
      const a = po.orderAmount ?? po.amount;
      setAmount(a != null && a !== "" ? String(a) : "");
    }
    setRemarks(String(po.remarks ?? "").trim());
    setSendType(getFieldString(po, ["sendType", "orderType", "type"], ""));
    setPreparedBy(getFieldString(po, ["orderPreparedBy", "preparedBy", "prepared_by"], ""));
    setApproverEmail(getFieldString(po, ["approverEmail"], ""));
    setLinkedPoRef(getFieldString(po, ["linkedPoRef", "linkedPo", "purchaseOrderRef"], ""));
    setLinkedGrnRef(getFieldString(po, ["linkedGrnRef", "linkedGrn", "grnRef"], ""));
    loadSlNo();
    toast.success("Duplicated as new order — review and submit");
    setTimeout(() => branchRef.current?.focus?.(), 0);
  };

  const openPartyLedger = async (supplierName) => {
    const name = String(supplierName || "").trim();
    if (!name) return toast.error("Party name not found");
    try {
      const snap = await getDocs(collection(db, "parties"));
      const parties = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const normalize = (v) => String(v || "").trim().toLowerCase();
      const squeeze = (v) =>
        normalize(v)
          .replace(/[^a-z0-9]/g, "")
          .replace(/unit0+(\d+)/g, "unit$1");
      const supplierNorm = normalize(name);
      const supplierCompact = squeeze(name);
      const target =
        parties.find((p) => normalize(p.partyName) === supplierNorm) ||
        parties.find((p) => squeeze(p.partyName) === supplierCompact) ||
        parties.find((p) => {
          const partyName = normalize(p.partyName);
          return partyName && (partyName.includes(supplierNorm) || supplierNorm.includes(partyName));
        }) ||
        parties.find((p) => {
          const compactName = squeeze(p.partyName);
          return compactName && (compactName.includes(supplierCompact) || supplierCompact.includes(compactName));
        });
      if (!target?.id) return toast.error(`Party not found for "${name}"`);
      navigate(`/party-order-history/${target.id}`);
    } catch (e) {
      toast.error(`Unable to open party ledger: ${e?.message || e}`);
    }
  };

  const mergeUploadFilesToOrder = async (orderId, files, mergeBase) => {
    const list = files ? Array.from(files) : [];
    if (!list.length) return null;
    if (!canAttach) throw new Error("No permission to upload");

    let base = mergeBase;
    if (!Array.isArray(base)) {
      const snap = await getDoc(doc(db, "orders", orderId));
      const data = snap.data() || {};
      base = Array.isArray(data.attachments) ? data.attachments : [];
    }

    const uploaded = [];
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      const safeName = String(file.name || "file").replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `orders/${orderId}/${Date.now()}_${i}_${safeName}`;
      const fref = storageRef(storage, path);
      await uploadBytes(fref, file);
      const url = await getDownloadURL(fref);
      uploaded.push({ name: file.name, url, uploadedAt: new Date().toISOString() });
    }
    const newArr = [...base, ...uploaded];
    await updateDoc(doc(db, "orders", orderId), {
      attachments: newArr,
      updatedAt: serverTimestamp(),
    });
    return newArr;
  };

  const uploadAttachmentsForOrder = async (orderId, files) => {
    if (!orderId) return;
    const list = files ? Array.from(files) : [];
    if (!list.length) return;
    if (!canAttach) return toast.error("No permission to upload");

    setBusyOrderId(orderId);
    try {
      const mergeBase = orderId === editingOrderId ? editingAttachments : undefined;
      const newArr = await mergeUploadFilesToOrder(orderId, list, mergeBase);
      if (newArr && orderId === editingOrderId) setEditingAttachments(newArr);
      toast.success("Attachments uploaded");
    } catch (e) {
      console.error(e);
      toast.error(`Upload failed: ${e?.message || e}`);
    } finally {
      setBusyOrderId(null);
    }
  };

  const appendHistoryEntry = (prevData, fromWf, toWf, comment) => {
    const hist = Array.isArray(prevData?.statusHistory) ? [...prevData.statusHistory] : [];
    hist.push({
      at: Timestamp.now(),
      from: fromWf,
      to: toWf,
      by: auth.currentUser?.displayName || auth.currentUser?.email || "user",
      byEmail: auth.currentUser?.email || null,
      comment: String(comment || "").trim(),
    });
    return hist;
  };

  const canApproveThisRow = (row) => {
    if (role === "admin" || role === "accounts") return true;
    if (hasPermission(storedUser, "pending_orders")) return true;
    const ap = String(row?.approverEmail || "").trim().toLowerCase();
    if (!ap) return false;
    return ap === currentUserEmail;
  };

  const applyWorkflowTransition = async (row, toWf, comment = "", opts = {}) => {
    const quiet = !!opts.quiet;
    if (!row?.id) return;
    const fromWf = row.workflow || getWorkflowStatus(row.raw);
    if (!allowedNextStatuses(fromWf).includes(toWf)) {
      if (!quiet) toast.error(`Cannot move from ${fromWf} to ${toWf}`);
      return;
    }
    setBusyOrderId(row.id);
    try {
      const snap = await getDoc(doc(db, "orders", row.id));
      const data = snap.data() || {};
      const hist = appendHistoryEntry(data, fromWf, toWf, comment);
      const payload = {
        workflowStatus: toWf,
        statusHistory: hist,
        updatedAt: serverTimestamp(),
      };
      if (toWf === WORKFLOW.RECEIVED) {
        payload.status = "received";
        payload.orderReceived = true;
        payload.receivedDate = serverTimestamp();
        payload.arrivedDate = Timestamp.fromDate(new Date());
      } else if (toWf === WORKFLOW.APPROVED) {
        payload.status = "approved";
      } else if (toWf === WORKFLOW.ORDERED) {
        payload.status = "ordered";
      }
      await updateDoc(doc(db, "orders", row.id), payload);
      if (toWf === WORKFLOW.RECEIVED) {
        await audit("received", row.id, {
          branch: normalizeBranchValue(row?.raw?.branch),
          supplier: row?.supplier,
          brand: row?.brand,
          fromWorkflow: fromWf,
          comment,
        });
      } else {
        await audit("workflow", row.id, { from: fromWf, to: toWf, comment });
      }
      if (!quiet) toast.success("Status updated");
      setTransitionOpen(null);
      setTransitionComment("");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Update failed");
    } finally {
      setBusyOrderId(null);
    }
  };

  const resendPartyEmail = async (row, { force = false } = {}) => {
    if (!row?.id) return;
    if (String(row.sendType || "").trim().toLowerCase() !== "email") {
      return toast.error("Order type must be Email to send supplier mail.");
    }
    setBusyOrderId(row.id);
    try {
      const res = await sendPendingOrderPartyAttachmentEmail({ orderId: row.id, force });
      toastPartyEmailResult(res);
    } catch (em) {
      console.error("resendPartyEmail", em);
      toast.error(em?.message || "Email failed");
    } finally {
      setBusyOrderId(null);
    }
  };

  const cancelOrder = async (row, opts = {}) => {
    if (!canCancel) return toast.error("No permission");
    if (!row?.id) return;
    if (!opts.skipConfirm && !window.confirm("Cancel this order?")) return;
    setBusyOrderId(row.id);
    try {
      const snap = await getDoc(doc(db, "orders", row.id));
      const data = snap.data() || {};
      const fromWf = getWorkflowStatus(data);
      const hist = appendHistoryEntry(data, fromWf, "cancelled", "Order cancelled");
      await updateDoc(doc(db, "orders", row.id), {
        status: "cancelled",
        statusHistory: hist,
        updatedAt: serverTimestamp(),
      });
      await audit("cancel", row.id, {
        branch: normalizeBranchValue(row?.raw?.branch),
        supplier: row?.supplier,
        brand: row?.brand,
      });
      if (!opts.skipToast) toast.success("Order cancelled");
    } catch (e) {
      console.error(e);
      toast.error(`Cancel failed: ${e?.message || e}`);
    } finally {
      setBusyOrderId(null);
    }
  };

  const submitOrder = async () => {
    if (submitting) return;

    const supplierTrim = supplier.trim();
    const brandTrim = brand.trim();
    if (!supplierTrim) return toast.error("Select supplier");
    if (!brandTrim) return toast.error("Enter brand");
    if (!qty.trim()) return toast.error("Enter quantity");
    const q = parseInt(qty.trim(), 10);
    if (!Number.isFinite(q) || q <= 0) return toast.error("Quantity must be > 0");
    if (q > _maxQty) return toast.error(`Quantity cannot exceed ${_maxQty}`);

    // Amount is now MANDATORY so every order doc carries a real `amount` field
    // in Firestore (downstream reports / Order Reports page rely on it).
    const amountTrim = amount.trim();
    if (!amountTrim) return toast.error("Enter order amount");
    const parsedAmount = parseFloat(amountTrim);
    if (!Number.isFinite(parsedAmount)) return toast.error("Amount must be a valid number");
    if (parsedAmount <= 0) return toast.error("Amount must be greater than 0");
    if (parsedAmount > _maxAmount) return toast.error(`Amount cannot exceed ${_maxAmount}`);

    if (!sendType.trim()) return toast.error("Select order type");
    if (!preparedBy.trim()) return toast.error("Select prepared by");
    if (!(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) return toast.error("Pick date");
    if (stagedFiles.length > 0 && !canAttach) return toast.error("No permission to upload order files");

    // Duplicate protection: same supplier+brand+qty pending on same day+branch
    const dayKey = ymd(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()));
    const dup = orders.find((po) => {
      if (!po) return false;
      if (editingOrderId && po.id === editingOrderId) return false;
      const branch = normalizeBranchValue(po.branch);
      if (branch !== formBranch) return false;
      if (!computePendingFlag(po)) return false;
      const dt = toDate(po.date ?? po.orderDate ?? po.createdAt);
      const key = dt ? ymd(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())) : "";
      if (key !== dayKey) return false;
      const sup = getFieldString(po, ["supplier", "party", "vendor"], "").toLowerCase();
      const br = String(po.brand ?? "").trim().toLowerCase();
      const qq = String(po.quantity ?? "").trim();
      return sup === supplierTrim.toLowerCase() && br === brandTrim.toLowerCase() && qq === String(q);
    });
    if (dup) {
      const ok = window.confirm(
        `Duplicate warning:\nA pending order already exists for same Supplier + Brand + Qty on ${dayKey} (${formBranch}).\n\nDo you want to continue?`
      );
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const user = auth.currentUser;
      const createdBy = user?.displayName ?? user?.email ?? "Unknown";
      const wasEditingId = editingOrderId;

      // Port: store date as Timestamp from selectedDate.
      const orderDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

      const base = {
        slNo,
        date: Timestamp.fromDate(orderDate),
        supplier: supplierTrim,
        brand: brandTrim,
        quantity: q,
        // Single canonical field for the order value. Older docs may still
        // carry `amount`; all read sites fall back to it for compatibility.
        orderAmount: parsedAmount,
        sendType: sendType.trim(),
        remarks: remarks.trim(),
        orderPreparedBy: preparedBy.trim(),
        branch: formBranch,
        approverEmail: approverEmail.trim() || null,
        linkedPoRef: linkedPoRef.trim() || null,
        linkedGrnRef: linkedGrnRef.trim() || null,
        updatedAt: serverTimestamp(),
      };

      let savedOrderId = wasEditingId;

      if (wasEditingId) {
        if (!canEdit) {
          toast.error("No permission to edit");
          return;
        }
        await updateDoc(doc(db, "orders", wasEditingId), {
          ...base,
          status: "pending",
          orderReceived: false,
        });
        await audit("edit", wasEditingId, {
          supplier: supplierTrim,
          brand: brandTrim,
          quantity: q,
          amount: parsedAmount,
          branch: formBranch,
        });
        toast.success("Order updated");
      } else {
        const ref = await addDoc(collection(db, "orders"), {
          ...base,
          status: "pending",
          orderReceived: false,
          workflowStatus: WORKFLOW.SUBMITTED,
          statusHistory: [
            {
              at: Timestamp.now(),
              from: null,
              to: WORKFLOW.SUBMITTED,
              by: createdBy,
              byEmail: user?.email || null,
              comment: "Submitted",
            },
          ],
          createdAt: serverTimestamp(),
          createdBy,
        });
        savedOrderId = ref.id;
        await audit("create", ref.id, {
          supplier: supplierTrim,
          brand: brandTrim,
          quantity: q,
          amount: parsedAmount,
          branch: formBranch,
        });
        toast.success("Order submitted");
      }

      let tryPartyEmail = Boolean(savedOrderId);
      if (stagedFiles.length > 0 && savedOrderId) {
        tryPartyEmail = false;
        setBusyOrderId(savedOrderId);
        try {
          const mergeBase = wasEditingId ? editingAttachments : [];
          const newArr = await mergeUploadFilesToOrder(savedOrderId, stagedFiles, mergeBase);
          if (wasEditingId && newArr) setEditingAttachments(newArr);
          setStagedFiles([]);
          tryPartyEmail = true;
        } catch (up) {
          console.error(up);
          toast.error(`Order saved, but file upload failed: ${up?.message || up}`);
        } finally {
          setBusyOrderId(null);
        }
      }

      if (tryPartyEmail && savedOrderId && sendType.trim().toLowerCase() === "email") {
        try {
          const res = await sendPendingOrderPartyAttachmentEmail({ orderId: savedOrderId });
          if (!toastPartyEmailResult(res, { quietSkips: true }) && res?.data?.reason === "no_party_email") {
            toast(
              "Order saved — add an email on the supplier party in Party master to send order notifications automatically.",
              { icon: "ℹ️", duration: 5000 }
            );
          }
        } catch (em) {
          console.warn("sendPendingOrderPartyAttachmentEmail", em);
          toast.error(`Order saved, but supplier email failed: ${em?.message || em}`);
        }
      }

      // Save "last order" for Repeat Last button.
      saveLastOrder();
      clearDraft();

      // Reset form.
      setSupplier("");
      setBrand("");
      setQty("");
      setAmount("");
      setRemarks("");
      setSendType("");
      setPreparedBy("");
      setApproverEmail("");
      setLinkedPoRef("");
      setLinkedGrnRef("");
      setSelectedDate(new Date());
      setEditingOrderId(null);
      setEditingAttachments([]);
      setStagedFiles([]);
      setSupplierMenuOpen(false);

      // optimistic slNo bump
      setSlNo((s) => (Number.isFinite(s) ? s + 1 : 1));
      // refresh masters in background
      loadMasters();
    } catch (e) {
      console.error(e);
      toast.error(`Submit failed: ${e?.message || String(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const approvalQueueCount = useMemo(() => {
    let n = 0;
    for (const po of orders) {
      const branch = normalizeBranchValue(po?.branch);
      if (branch !== selectedBranch) continue;
      if (!computePendingFlag(po)) continue;
      if (getWorkflowStatus(po) !== WORKFLOW.SUBMITTED) continue;
      const ap = String(po.approverEmail || "").trim().toLowerCase();
      if (!ap || ap === currentUserEmail) n += 1;
    }
    return n;
  }, [orders, selectedBranch, currentUserEmail]);

  useEffect(() => {
    setSelectedIds([]);
  }, [selectedBranch, listTab]);

  useEffect(() => {
    if (!timelineFor) {
      setTimelineRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setTimelineLoading(true);
      try {
        const qy = query(collection(db, "auditLogs"), where("entityId", "==", timelineFor), limit(100));
        const snap = await getDocs(qy);
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => String(r.entity || "") === "order");
        rows.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
        if (!cancelled) setTimelineRows(rows);
      } catch (e) {
        console.warn(e);
        if (!cancelled) setTimelineRows([]);
      } finally {
        if (!cancelled) setTimelineLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [timelineFor]);

  const approvalToastRef = useRef(false);
  useEffect(() => {
    if (approvalQueueCount > 0 && !approvalToastRef.current) {
      approvalToastRef.current = true;
      toast(`Approval queue: ${approvalQueueCount} order(s) may need review (check Approval tab)`, { duration: 4500 });
    }
    if (approvalQueueCount === 0) approvalToastRef.current = false;
  }, [approvalQueueCount]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllVisible = () => {
    if (selectedIds.length === pendingList.length && pendingList.length) setSelectedIds([]);
    else setSelectedIds(pendingList.map((x) => x.id));
  };

  const bulkExportCsv = () => {
    const rows = selectedIds.length ? pendingList.filter((r) => selectedIds.includes(r.id)) : pendingList;
    if (!rows.length) return toast.error("Nothing to export");
    const header = ["supplier", "brand", "date", "amount", "workflow", "daysOpen", "preparedBy", "linkedPo", "linkedGrn"];
    const lines = [header.join(",")].concat(
      rows.map((o) =>
        [o.supplier, o.brand, ymd(o.date), o.amount, o.workflow, o.pendingDays, o.preparedBy, o.linkedPo, o.linkedGrn]
          .map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pending-orders-${selectedBranch}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  const bulkCancelSelected = async () => {
    if (!canCancel) return toast.error("No permission");
    if (!selectedIds.length) return;
    if (!window.confirm(`Cancel ${selectedIds.length} order(s)?`)) return;
    for (const id of selectedIds) {
      const row = pendingList.find((r) => r.id === id);
      if (row) await cancelOrder(row, { skipConfirm: true, skipToast: true });
    }
    setSelectedIds([]);
    toast.success("Cancelled selected orders");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />

      <main className="mx-auto max-w-6xl p-4 pb-24 pt-4 sm:p-5 lg:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Pending orders</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Pending count: {pendingCount}
            </p>
          </div>

          <button
            type="button"
            onClick={openNewOrder}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New order
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
          <button
            type="button"
            onClick={() => setListTab("pending")}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
              listTab === "pending" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All pending
          </button>
          <button
            type="button"
            onClick={() => setListTab("approval")}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
              listTab === "approval" ? "bg-amber-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Approval queue
            {approvalQueueCount > 0 ? (
              <span className="ml-1.5 rounded-full bg-white/20 px-1.5 py-0.5 text-xs">{approvalQueueCount}</span>
            ) : null}
          </button>
          <span className="hidden text-gray-300 sm:inline">|</span>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            Sort
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
            >
              <option value="date">Order date (newest)</option>
              <option value="aging">Days open (worst first)</option>
            </select>
          </label>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-950">
            <span className="font-semibold">{selectedIds.length} selected</span>
            <button
              type="button"
              onClick={selectAllVisible}
              className="rounded-lg border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold hover:bg-indigo-100"
            >
              {selectedIds.length === pendingList.length && pendingList.length ? "Deselect all" : "Select all"}
            </button>
            <button type="button" onClick={() => setSelectedIds([])} className="text-xs font-semibold text-indigo-700 underline">
              Clear
            </button>
            {canCancel && (
              <button
                type="button"
                onClick={bulkCancelSelected}
                className="rounded-lg bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-rose-700"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={bulkExportCsv}
              className="rounded-lg border border-indigo-300 bg-white px-2.5 py-1 text-xs font-semibold text-indigo-900 hover:bg-white"
            >
              Export CSV
            </button>
          </div>
        )}

        {/* Branch-wise summary cards */}
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {branchSummary.map((s) => (
            <button
              key={s.branch}
              type="button"
              onClick={() => setSelectedBranch(s.branch)}
              className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition hover:shadow-md ${
                selectedBranch === s.branch ? "border-indigo-300 ring-2 ring-indigo-200" : "border-gray-200"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Branch</p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {visibleBranches.find((b) => b.value === s.branch)?.label ?? s.branch}
                  </p>
                </div>
                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-800 border border-amber-100">
                  Pending {s.pending}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-gray-100 bg-gray-50 px-2 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Received</p>
                  <p className="mt-0.5 text-sm font-bold text-emerald-700">{s.receivedToday}</p>
                  <p className="text-[10px] text-gray-400">Today</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 px-2 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Cancelled</p>
                  <p className="mt-0.5 text-sm font-bold text-rose-700">{s.cancelledToday}</p>
                  <p className="text-[10px] text-gray-400">Today</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 px-2 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Pending ₹</p>
                  <p className="mt-0.5 text-sm font-bold text-indigo-700">{Math.round(s.pendingAmount)}</p>
                  <p className="text-[10px] text-gray-400">Total</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Advanced filters — compact */}
        <div className="mb-2 rounded-md border border-gray-200 bg-white p-1.5 shadow-sm">
          <div className="mb-1 flex items-center justify-between gap-2">
            <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-400">Filters</p>
            <button
              type="button"
              onClick={() => {
                setFilterFrom("");
                setFilterTo("");
                setFilterPreparedBy("");
                setFilterOrderType("");
                setFilterBrand("");
                setFilterAmountMin("");
                setFilterAmountMax("");
                setAgingAlertEnabled(false);
                setAgingThresholdDays(14);
              }}
              className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
          </div>

          <div className="grid grid-cols-2 gap-x-2 gap-y-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
            <div className="min-w-0">
              <label className="block text-[9px] leading-none text-gray-500">From</label>
              <input
                type="date"
                className="mt-0.5 h-7 w-full rounded border border-gray-300 bg-white px-1 py-0 text-[11px] leading-none"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-[9px] leading-none text-gray-500">To</label>
              <input
                type="date"
                className="mt-0.5 h-7 w-full rounded border border-gray-300 bg-white px-1 py-0 text-[11px] leading-none"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-[9px] leading-none text-gray-500">Prepared</label>
              <select
                className="mt-0.5 h-7 w-full rounded border border-gray-300 bg-white px-1 py-0 text-[11px]"
                value={filterPreparedBy}
                onChange={(e) => setFilterPreparedBy(e.target.value)}
              >
                <option value="">All</option>
                {preparedByOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-[9px] leading-none text-gray-500">Type</label>
              <select
                className="mt-0.5 h-7 w-full rounded border border-gray-300 bg-white px-1 py-0 text-[11px]"
                value={filterOrderType}
                onChange={(e) => setFilterOrderType(e.target.value)}
              >
                <option value="">All</option>
                {orderTypeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0 sm:col-span-2 lg:col-span-2">
              <label className="block text-[9px] leading-none text-gray-500">Brand</label>
              <input
                className="mt-0.5 h-7 w-full rounded border border-gray-300 bg-white px-1.5 py-0 text-[11px]"
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                placeholder="Contains…"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-[9px] leading-none text-gray-500">Min ₹</label>
              <input
                type="number"
                className="mt-0.5 h-7 w-full rounded border border-gray-300 bg-white px-1.5 py-0 text-[11px]"
                value={filterAmountMin}
                onChange={(e) => setFilterAmountMin(e.target.value)}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-[9px] leading-none text-gray-500">Max ₹</label>
              <input
                type="number"
                className="mt-0.5 h-7 w-full rounded border border-gray-300 bg-white px-1.5 py-0 text-[11px]"
                value={filterAmountMax}
                onChange={(e) => setFilterAmountMax(e.target.value)}
              />
            </div>
            <div className="col-span-2 flex min-w-0 flex-wrap items-end gap-x-2 gap-y-0.5 sm:col-span-3 lg:col-span-2">
              <label className="inline-flex h-7 items-center gap-1 rounded border border-gray-200 bg-gray-50 px-1.5 text-[10px] text-gray-700">
                <input
                  type="checkbox"
                  className="h-3 w-3"
                  checked={agingAlertEnabled}
                  onChange={(e) => setAgingAlertEnabled(e.target.checked)}
                />
                Aging
              </label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={1}
                  className="h-7 w-12 rounded border border-gray-300 bg-white px-1 py-0 text-[11px]"
                  value={agingThresholdDays}
                  onChange={(e) => setAgingThresholdDays(Number(e.target.value))}
                />
                <span className="text-[10px] text-gray-500">d</span>
              </div>
            </div>
          </div>
        </div>

        {loadingPending ? (
          <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-600">Loading pending orders...</div>
        ) : pendingList.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-sm text-gray-600 text-center">No pending orders found.</div>
        ) : (
          <div className="space-y-3">
            {pendingList.map((o) => {
              const bandClass = slaBandClass(o.sla);
              return (
                <div key={o.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                  <div className="mt-1 flex flex-col items-center gap-1">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={selectedIds.includes(o.id)}
                      onChange={() => toggleSelect(o.id)}
                      aria-label="Select order"
                    />
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 border border-red-100 text-red-700 font-semibold text-xs">
                      P
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-gray-900 truncate">{o.supplier}</p>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 border border-gray-200 rounded px-1.5 py-0.5">
                        {o.workflow}
                      </span>
                      {String(o.sendType || "").trim().toLowerCase() === "email" ? (() => {
                        const badge = partyEmailStatusBadge(o);
                        if (!badge) return null;
                        return (
                          <span
                            title={badge.title || ""}
                            className={`max-w-[11rem] truncate text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        );
                      })() : null}
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${bandClass}`}>
                        {o.pendingDays}d open
                      </span>
                      <span className="ml-auto text-xs text-gray-500">{o.attachments?.length ? `${o.attachments.length} file(s)` : ""}</span>
                    </div>

                    <div className="mt-2 text-xs text-gray-600 space-y-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span>
                          <span className="text-gray-500">Invoice:</span> {o.invoiceNo}
                        </span>
                        <span>
                          <span className="text-gray-500">Brand:</span> {o.brand}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Date:</span> {fmtDateTime(o.date)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span>
                          <span className="text-gray-500">Amount:</span> ₹{Number(o.amount).toFixed(2)}
                        </span>
                        <span>
                          <span className="text-gray-500">Prepared:</span> {o.preparedBy}
                        </span>
                        {o.approverEmail ? (
                          <span>
                            <span className="text-gray-500">Approver:</span> {o.approverEmail}
                          </span>
                        ) : null}
                      </div>
                      {(o.linkedPo || o.linkedGrn) && (
                        <div className="flex flex-wrap gap-2 text-[11px]">
                          {o.linkedPo ? (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5">
                              PO: {o.linkedPo}
                            </span>
                          ) : null}
                          {o.linkedGrn ? (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5">
                              GRN: {o.linkedGrn}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => openPartyLedger(o.supplier)}
                        className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        History
                      </button>
                      {String(o.sendType || "").trim().toLowerCase() === "email" &&
                      o.workflow !== WORKFLOW.CANCELLED ? (
                        <button
                          type="button"
                          disabled={busyOrderId === o.id}
                          onClick={() =>
                            resendPartyEmail(o, {
                              force: ["sent", "skipped", "failed"].includes(String(o.partyEmailStatus || "")),
                            })
                          }
                          className="rounded-lg border border-indigo-200 bg-indigo-50 px-2 py-1 text-[10px] font-semibold text-indigo-900 hover:bg-indigo-100 disabled:opacity-50"
                          title="Send or resend supplier email with attachments"
                        >
                          {busyOrderId === o.id
                            ? "…"
                            : ["sent", "skipped", "failed"].includes(String(o.partyEmailStatus || ""))
                              ? "Resend"
                              : "Email"}
                        </button>
                      ) : null}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => duplicateOrder(o)}
                          className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-[10px] font-semibold text-red-900 hover:bg-red-100"
                        >
                          Duplicate
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          disabled={busyOrderId === o.id}
                          onClick={() => openEditOrder(o)}
                          className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Edit
                        </button>
                      )}
                      {o.workflow === WORKFLOW.SUBMITTED && canApproveThisRow(o) && (
                        <button
                          type="button"
                          disabled={busyOrderId === o.id}
                          onClick={() => {
                            setTransitionComment("");
                            setTransitionOpen({ id: o.id, row: o, to: WORKFLOW.APPROVED, label: "Approve" });
                          }}
                          className="rounded-lg bg-violet-600 px-2 py-1.5 text-[10px] font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                        >
                          Approve
                        </button>
                      )}
                      {o.workflow === WORKFLOW.APPROVED && canApproveThisRow(o) && (
                        <button
                          type="button"
                          disabled={busyOrderId === o.id}
                          onClick={() => {
                            setTransitionComment("");
                            setTransitionOpen({ id: o.id, row: o, to: WORKFLOW.ORDERED, label: "Mark ordered" });
                          }}
                          className="rounded-lg bg-blue-600 px-2 py-1.5 text-[10px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          Ordered
                        </button>
                      )}
                      {canCancel && (
                        <button
                          type="button"
                          disabled={busyOrderId === o.id}
                          onClick={() => cancelOrder(o)}
                          className="rounded-lg bg-rose-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>

                    {canAttach && (
                      <label className="cursor-pointer rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50">
                        {busyOrderId === o.id ? "Uploading..." : "Attach"}
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          disabled={busyOrderId === o.id}
                          onChange={(e) => {
                            const picked = Array.from(e.target.files || []);
                            e.target.value = "";
                            uploadAttachmentsForOrder(o.id, picked);
                          }}
                        />
                      </label>
                    )}
                  </div>
                  </div>

                  {Array.isArray(o.attachments) && o.attachments.length > 0 && (
                    <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">Attachments</p>
                      <ul className="mt-1 space-y-1 text-xs">
                        {o.attachments.slice(0, 3).map((a, idx) => (
                          <li key={idx} className="truncate">
                            <a className="text-indigo-700 hover:underline" href={a.url} target="_blank" rel="noreferrer">
                              {a.name || a.url}
                            </a>
                          </li>
                        ))}
                        {o.attachments.length > 3 && (
                          <li className="text-gray-500">+{o.attachments.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {orderModalOpen && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            aria-label="Close"
            onClick={() => setOrderModalOpen(false)}
          />
          <div className="relative z-10 w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
            <div className="flex items-start justify-between gap-2 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white px-2.5 py-2 sm:px-3 sm:py-2.5">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{editingOrderId ? "Edit order" : "New order"}</h2>
                <p className="mt-0.5 text-sm text-red-700">
                  <span className="font-semibold text-gray-900">SL:</span> {slNo}
                  {editingOrderId ? <span className="ml-2 text-xs text-gray-500">• Editing</span> : null}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOrderModalOpen(false)}
                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="max-h-[78vh] overflow-y-auto px-2.5 py-2.5 sm:px-3 sm:py-3">
              <div className="mb-2 flex flex-col gap-1.5 sm:flex-row sm:items-end sm:gap-1.5">
                <div className="min-w-0 flex-1">
                  <label className="text-xs font-semibold text-gray-600">Branch</label>
                  <select
                    ref={branchRef}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={formBranch}
                    onChange={(e) => setFormBranch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        focusNext(dateRef);
                      }
                    }}
                  >
                    {visibleBranches.map((b) => (
                      <option key={b.value} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0 flex-1 sm:max-w-[11rem]">
                  <label className="text-xs font-semibold text-gray-600">Order date</label>
                  <input
                    ref={dateRef}
                    type="date"
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={fmtDateForInput(selectedDate)}
                    onChange={(e) => setSelectedDate(new Date(e.target.value + "T00:00:00"))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        focusNext(supplierRef);
                      }
                    }}
                  />
                </div>
                <div className="shrink-0 sm:pb-[1px]">
                  <button
                    type="button"
                    onClick={() => {
                      loadSlNo();
                      // Clear all form fields after refreshing SL number.
                      clearDraft();
                      setFormBranch(selectedBranch);
                      setSupplier("");
                      setBrand("");
                      setQty("");
                      setAmount("");
                      setRemarks("");
                      setSendType("");
                      setPreparedBy("");
                      setSelectedDate(new Date());
                      setEditingOrderId(null);
                      setEditingAttachments([]);
                      setStagedFiles([]);
                      setSupplierMenuOpen(false);
                      // After state updates, return focus to Branch dropdown.
                      setTimeout(() => {
                        branchRef.current?.focus?.();
                      }, 0);
                    }}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50 sm:w-auto"
                  >
                    Refresh SL
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-x-1.5 gap-y-2 md:grid-cols-2">
                <div className="md:col-span-2 relative" ref={supplierWrapRef}>
                  <label className="text-xs font-semibold text-gray-600">Supplier</label>
                  <div className="mt-1 flex gap-0.5">
                    <input
                      ref={supplierRef}
                      autoComplete="off"
                      className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      placeholder="Type to filter, or pick from list"
                      value={supplier}
                      onChange={(e) => {
                        setSupplier(e.target.value);
                        setSupplierMenuOpen(true);
                        setSupplierHighlightIdx(-1);
                      }}
                      onFocus={() => setSupplierMenuOpen(true)}
                      role="combobox"
                      aria-expanded={supplierMenuOpen}
                      aria-controls="supplier-party-listbox"
                      aria-autocomplete="list"
                      onKeyDown={(e) => {
                        const list = supplierPickList;
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          if (!supplierMenuOpen) setSupplierMenuOpen(true);
                          if (!list.length) return;
                          setSupplierHighlightIdx((i) => (i < 0 ? 0 : Math.min(i + 1, list.length - 1)));
                          return;
                        }
                        if (e.key === "ArrowUp") {
                          if (!supplierMenuOpen || !list.length) return;
                          e.preventDefault();
                          setSupplierHighlightIdx((i) => (i <= 0 ? 0 : i - 1));
                          return;
                        }
                        if (e.key === "Enter") {
                          if (supplierMenuOpen && list.length) {
                            const exact = list.findIndex((x) => x.toLowerCase() === supplier.trim().toLowerCase());
                            const idx =
                              supplierHighlightIdx >= 0 ? supplierHighlightIdx : exact >= 0 ? exact : -1;
                            if (idx >= 0 && idx < list.length) {
                              e.preventDefault();
                              setSupplier(list[idx]);
                              setSupplierMenuOpen(false);
                              focusNext(brandRef);
                              return;
                            }
                          }
                          e.preventDefault();
                          setSupplierMenuOpen(false);
                          focusNext(brandRef);
                          return;
                        }
                        if (e.key === "Escape") {
                          if (supplierMenuOpen) {
                            e.preventDefault();
                            e.stopPropagation();
                            setSupplierMenuOpen(false);
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      aria-expanded={supplierMenuOpen}
                      aria-label="Toggle party list"
                      onClick={() => setSupplierMenuOpen((o) => !o)}
                      className="shrink-0 rounded-lg border border-gray-300 bg-gray-50 px-2.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                    >
                      ▼
                    </button>
                  </div>
                  <p className="mt-0.5 text-[10px] text-gray-500">
                    {supplierOptionsRanked.length} parties — ↑↓ to move, Enter to choose, Esc to close list.
                  </p>
                  {supplierMenuOpen ? (
                    <div
                      ref={supplierListboxRef}
                      id="supplier-party-listbox"
                      role="listbox"
                      className="absolute left-0 right-0 z-[2000] mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl ring-1 ring-black/5"
                    >
                      {supplierPickList.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-500">No party names match. Clear text to see all.</div>
                      ) : (
                        supplierPickList.map((s, idx) => (
                          <button
                            key={s}
                            type="button"
                            role="option"
                            aria-selected={supplierHighlightIdx === idx}
                            data-supplier-option-index={idx}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSupplier(s);
                              setSupplierMenuOpen(false);
                            }}
                            onMouseEnter={() => setSupplierHighlightIdx(idx)}
                            className={`flex w-full px-3 py-1.5 text-left text-sm text-gray-900 ${
                              supplierHighlightIdx === idx ? "bg-indigo-100" : "hover:bg-indigo-50"
                            }`}
                          >
                            {s}
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="md:col-span-2">
                  <div className="flex flex-wrap items-end gap-1.5 md:flex-nowrap">
                    <div className="min-w-[110px] flex-1">
                      <label className="text-xs font-semibold text-gray-600">Brand</label>
                      <input
                        ref={brandRef}
                        list="brand_list"
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                        placeholder="Enter brand"
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            focusNext(qtyRef);
                          }
                        }}
                      />
                      <datalist id="brand_list">
                    {brandOptionsRanked.map((b) => (
                          <option key={b} value={b} />
                        ))}
                      </datalist>
                    </div>

                    <div className="w-[100px] shrink-0">
                      <label className="text-xs font-semibold text-gray-600">Quantity</label>
                      <input
                        ref={qtyRef}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-sm"
                        value={qty}
                        min={0}
                        onChange={(e) => setQty(sanitizeQty(e.target.value))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            focusNext(amountRef);
                          }
                        }}
                      />
                    </div>

                    <div className="min-w-[100px] flex-1">
                      <label className="text-xs font-semibold text-gray-600">
                        Amount <span className="text-rose-600">*</span>
                      </label>
                      <input
                        ref={amountRef}
                        type="number"
                        required
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                        value={amount}
                        min={0}
                        step="0.01"
                        placeholder="Order value (₹)"
                        onChange={(e) => setAmount(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            focusNext(remarksRef);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-gray-600">Remarks</label>
                  <textarea
                    ref={remarksRef}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Any notes..."
                    onKeyDown={(e) => {
                      // Enter moves to next field; Shift+Enter keeps newline.
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        focusNext(sendTypeRef);
                      }
                    }}
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600">Order type</label>
                  <select
                    ref={sendTypeRef}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={sendType}
                    onChange={(e) => setSendType(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        focusNext(preparedByRef);
                      }
                    }}
                  >
                    <option value="">Select order type</option>
                    {orderTypeOptions.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600">Prepared by</label>
                  <select
                    ref={preparedByRef}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    value={preparedBy}
                    onChange={(e) => setPreparedBy(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        focusNext(submitRef);
                      }
                    }}
                  >
                    <option value="">Select preparer</option>
                    {preparedByOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-gray-600">Approver email (optional)</label>
                  <input
                    type="email"
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    placeholder="Who must approve (must match sign-in email for approval queue)"
                    value={approverEmail}
                    onChange={(e) => setApproverEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Linked PO #</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    placeholder="Purchase order ref."
                    value={linkedPoRef}
                    onChange={(e) => setLinkedPoRef(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Linked GRN #</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    placeholder="Goods receipt ref."
                    value={linkedGrnRef}
                    onChange={(e) => setLinkedGrnRef(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-dashed border-gray-200 bg-gray-50/90 p-2.5">
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <div>
                    <p className="text-xs font-semibold text-gray-800">Order file(s) (optional)</p>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                      Stored in Firebase when you save. If <span className="font-medium text-gray-700">Order type</span>{" "}
                      is <span className="font-medium text-gray-700">Email</span>, the supplier is emailed on submit
                      (attachments included when you add files; otherwise a short summary). Requires SMTP under{" "}
                      <span className="font-medium text-gray-700">Tools &amp; Utilities → Pending order email</span>{" "}
                      and an email on the party master.
                    </p>
                  </div>
                  {canAttach ? (
                    <label className="cursor-pointer shrink-0 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50">
                      Add files
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          // Copy before clearing: resetting value empties the live FileList.
                          const picked = Array.from(e.target.files || []);
                          e.target.value = "";
                          if (!picked.length) return;
                          setStagedFiles((prev) => {
                            const seen = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
                            const fresh = picked.filter((f) => !seen.has(`${f.name}|${f.size}|${f.lastModified}`));
                            return [...prev, ...fresh];
                          });
                        }}
                      />
                    </label>
                  ) : null}
                </div>
                {stagedFiles.length > 0 ? (
                  <ul className="mt-2 space-y-1 border-t border-gray-100 pt-2 text-xs">
                    {stagedFiles.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-gray-800">{f.name}</span>
                        <button
                          type="button"
                          className="shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-50"
                          onClick={() => setStagedFiles((prev) => prev.filter((_, j) => j !== idx))}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-[11px] text-gray-500">No files queued for this save.</p>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    loadLastOrder();
                    toast.success("Loaded last order");
                  }}
                  className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-800 hover:bg-indigo-100"
                >
                  Repeat last order
                </button>

                <button
                  type="button"
                  onClick={submitOrder}
                  disabled={submitting}
                ref={submitRef}
                  className="ml-auto rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
                >
                  {submitting ? "Saving..." : editingOrderId ? "Update order" : "Submit order"}
                </button>
              </div>

              {editingOrderId && (
                <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-2.5">
                  <div className="flex items-center justify-between gap-1.5">
                    <p className="text-xs font-semibold text-gray-700">Attachments on file</p>
                    {canAttach && (
                      <label className="cursor-pointer rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-800 hover:bg-gray-50">
                        {busyOrderId === editingOrderId ? "Uploading..." : "Upload"}
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          disabled={busyOrderId === editingOrderId}
                          onChange={(e) => {
                            const picked = Array.from(e.target.files || []);
                            e.target.value = "";
                            uploadAttachmentsForOrder(editingOrderId, picked);
                          }}
                        />
                      </label>
                    )}
                  </div>
                  {editingAttachments.length === 0 ? (
                    <p className="mt-2 text-xs text-gray-500">No files</p>
                  ) : (
                    <ul className="mt-2 space-y-1 text-xs">
                      {editingAttachments.map((a, idx) => (
                        <li key={idx} className="truncate">
                          <a className="text-indigo-700 hover:underline" href={a.url} target="_blank" rel="noreferrer">
                            {a.name || a.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {transitionOpen && (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transition-modal-title"
        >
          <div className="bg-white rounded-xl shadow-xl p-5 max-w-md w-full border border-gray-200">
            <h3 id="transition-modal-title" className="text-lg font-semibold text-gray-900">
              {transitionOpen.label}
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Optional comment is saved on the order history and audit log.
            </p>
            <textarea
              className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[80px]"
              placeholder="Comment (optional)"
              value={transitionComment}
              onChange={(e) => setTransitionComment(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                onClick={() => {
                  setTransitionOpen(null);
                  setTransitionComment("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busyOrderId === transitionOpen.id}
                className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
                onClick={() =>
                  applyWorkflowTransition(
                    {
                      ...transitionOpen.row,
                      workflow: transitionOpen.row.workflow || getWorkflowStatus(transitionOpen.row.raw),
                    },
                    transitionOpen.to,
                    transitionComment
                  )
                }
              >
                {busyOrderId === transitionOpen.id ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {timelineFor && (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="timeline-modal-title"
        >
          <div className="bg-white rounded-xl shadow-xl p-5 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="flex items-center justify-between gap-2 shrink-0">
              <h3 id="timeline-modal-title" className="text-lg font-semibold text-gray-900">
                Activity
              </h3>
              <button
                type="button"
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                onClick={() => setTimelineFor(null)}
              >
                Close
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500 shrink-0">Audit trail for this order (newest first).</p>
            <div className="mt-3 flex-1 overflow-y-auto space-y-2 text-sm min-h-0">
              {timelineLoading ? (
                <p className="text-gray-500">Loading…</p>
              ) : timelineRows.length === 0 ? (
                <p className="text-gray-500">No audit entries yet.</p>
              ) : (
                timelineRows.map((r) => {
                  const ts = r.timestamp;
                  const when =
                    ts && typeof ts.toDate === "function"
                      ? fmtDateTime(ts.toDate())
                      : fmtDateTime(toDate(ts));
                  return (
                    <div key={r.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="font-semibold text-gray-900">{r.action || "—"}</span>
                        <span className="text-xs text-gray-500">{when}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        {r.byEmail || r.byName || r.by || "—"}
                      </div>
                      {r.extra && Object.keys(r.extra).length > 0 ? (
                        <pre className="mt-1 text-[11px] text-gray-600 whitespace-pre-wrap break-all font-sans">
                          {JSON.stringify(r.extra)}
                        </pre>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

